import {Component} from 'panel';

import {ENTER_KEY_CODE, SPACE_KEY_CODE} from './dom';

type FocusableElement = Component<any, any, unknown, {focusable: boolean; disabled: boolean}>;

export const focusableElements = [
  `a[href]`,
  `area[href]`,
  `input:not([disabled])`,
  `mp-checkbox:not([disabled])`,
  `mp-input:not([disabled])`,
  `select:not([disabled])`,
  `mp-select:not([disabled])`,
  `textarea:not([disabled])`,
  `button:not([disabled])`,
  `mp-button:not([disabled])`,
  `iframe`,
  `object`,
  `embed`,
  `[contenteditable]`,
  `[tabindex]:not([tabindex^="-"])`,
];

function handleKeyPress(ev: KeyboardEvent): void {
  if (ev.keyCode === ENTER_KEY_CODE || ev.keyCode === SPACE_KEY_CODE) {
    ev.preventDefault();
    ev.currentTarget.dispatchEvent(
      new MouseEvent(`click`, {
        view: window,
        bubbles: true,
        cancelable: true,
      }),
    );
  }
}

export function handleConnectedCallback(el: FocusableElement): void {
  // focusable is default true, so we will call handleAttr as if it was explicitly set
  handleAttributeChangedCallback(el, `focusable`);
}

export function handleAttributeChangedCallback(el: FocusableElement, attr: string): void {
  if (attr === `disabled` || attr === `focusable`) {
    const isFocusable = el.attr(`focusable`) && !el.attr(`disabled`);

    if (isFocusable) {
      el.addEventListener(`keypress`, handleKeyPress);
      el.setAttribute(`tabindex`, `0`);
    } else {
      el.removeEventListener(`keypress`, handleKeyPress);
      el.removeAttribute(`tabindex`);
    }
  }
}

export function handleDisconnectedCallback(el: FocusableElement): void {
  el.removeEventListener(`keypress`, handleKeyPress);
}

/**
 * This returns a list of children that are focusable. This includes any item with a non-negative tab index,
 * anchors, buttons, and custom mp-* objects. See the exported focusableElements for a list of all possible values
 */
export function getFocusableChildren(node) {
  return Array.from(node.querySelectorAll(focusableElements.join(`,`))).filter((child: HTMLElement) => {
    // if the node is not visible due to a size of 0, we do not want to it to be eligible
    return Boolean(child.offsetWidth || child.offsetHeight || child.getClientRects().length);
  });
}

/**
 * This gets the current active element on the page. It starts at the root document
 * and will pierce any ShadowDOM if it is encountered.
 */
export function getActiveElement(node: Document | ShadowRoot = document, eligibleNodes = []) {
  if (node.activeElement && eligibleNodes.includes(node.activeElement)) {
    return node.activeElement;
  } else if (node.activeElement && node.activeElement.shadowRoot) {
    return getActiveElement(node.activeElement.shadowRoot, eligibleNodes);
  }
  return node.activeElement;
}
